import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { DELETE_MESSAGE_ACTION_TYPE, resolvePubnubError, sleep } from './utils';
import { SliEvents } from '../../metrics/sliConstants';

const LOG = LoggerFactory.create('PubNubMessageAction');

// the max number of messages to be fetched in one history request.
// if no max results specified, default to 500.
const MAX_COUNT = 100;
const MAX_RETRIES = 3;
const RETRY_DELAY = 500;

/**
 *
 * @param {*} getPubNubClient
 * @param {*} channel
 * @returns object containing channel and notificationsIds
 */
export const geMessageActionsForChannel = async ({
  pubNubClient,
  channel,
  limit = MAX_COUNT,
  start,
  retry = 0,
  delay = RETRY_DELAY
}) => {
  const results = [];
  const nextRequest = {
    pubNubClient,
    channel,
    start
  };
  try {
    if (retry > 0) {
      // added exponential delay before retrying
      await sleep(retry * delay);
    }
    const response = await pubNubClient.getMessageActions({
      channel,
      start,
      limit
    });
    const { data, start: firstTT } = response || {};

    if (data?.length) {
      results.push(...data);
      nextRequest.start = firstTT;
    }
    if (data?.length === limit) {
      const nextResponse = await geMessageActionsForChannel(nextRequest);
      if (nextResponse.length) {
        results.push(...nextResponse);
      }
    }
  } catch (err) {
    const errorData = resolvePubnubError(err);
    const message = `Error in pubNub getMessageActions call for channel ${channel}}`;
    if (retry < MAX_RETRIES) {
      LOG.debug(`${message}. Retrying with count...${retry + 1}`, err, {
        status: err.status
      });
      return geMessageActionsForChannel({
        pubNubClient,
        channel,
        start,
        retry: retry + 1,
        delay: RETRY_DELAY
      });
    }
    LOG.error(`${message}. Retry exhausted!`, errorData);
    throw err;
  }
  return results;
};

export const getAndUpdateDeletedAnnouncementsForChannel = async ({
  channel,
  pubNubClient
}) => {
  try {
    const data = await geMessageActionsForChannel({
      pubNubClient,
      channel
    });
    const notificationIds = [];
    data.forEach(item => {
      if (item.type === DELETE_MESSAGE_ACTION_TYPE) {
        notificationIds.push(item.value);
      }
    });

    return notificationIds;
  } catch (e) {
    LOG.error(`Error while fetching messages actions for ${channel}`, e);
    throw e;
  }
};
/**
 * method to return all deleted message action announcement ids for the channels
 * @param {Object} param object containing channels, eventId, attendeeId and pubNub instance to fetch deleted message actions
 */
export const getDeletedAnnouncementIdsFromMessageAction = async ({
  attendeeId,
  eventId,
  channels,
  pubNubClient,
  successCallback,
  trackAction
}) => {
  try {
    const response = await Promise.all(
      channels.map(async channel =>
        getAndUpdateDeletedAnnouncementsForChannel({
          channel,
          pubNubClient
        })
      )
    );
    trackAction(SliEvents.NOTIFICATION_PUBNUB_MESSAGE_ACTIONS, {
      result: 'success',
      channels
    });
    const notificationIds = [...new Set(response.flat())];
    if (notificationIds?.length) {
      await successCallback(notificationIds);
    }
    return notificationIds;
  } catch (e) {
    const errorData = resolvePubnubError(e);
    const errorMessage = 'Failed to fetch deleted messages actions';
    trackAction(SliEvents.NOTIFICATION_PUBNUB_MESSAGE_ACTIONS, {
      result: 'error',
      channels,
      error: errorMessage,
      ...errorData
    });
    LOG.error(
      `${errorMessage} for eventId: ${eventId}, attendeeId: ${attendeeId}`,
      errorData
    );
    throw e;
  }
};
